import React, { FC, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Anchor, AnchorMode } from '../Anchor';
import { SITE_URLS } from '@constants';

if (L.Icon.Default) {
    L.Icon.Default.mergeOptions({
        iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
        iconUrl: require('leaflet/dist/images/marker-icon.png'),
        shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
    });
}

export interface locationProps {
    locations: {
        uuid: string;
        name: string;
        latitude: number;
        longitude: number;
    }[];
    style?: string;
}

const jitterLocations = (locations: locationProps['locations'], offset = 0.0001): locationProps['locations'] => {
    const grouped = new globalThis.Map<string, locationProps['locations']>();

    locations.forEach((location) => {
        const lat = Number(location.latitude);
        const lng = Number(location.longitude);
        const key = `${lat},${lng}`;
        if (!grouped.has(key)) {
            grouped.set(key, []);
        }
        grouped.get(key)!.push({ ...location, latitude: lat, longitude: lng });
    });

    const jittered: locationProps['locations'] = [];

    grouped.forEach((group) => {
        if (group.length === 1) {
            jittered.push(group[0]);
        } else {
            const angleStep = (2 * Math.PI) / group.length;
            const [baseLat, baseLng] = [group[0].latitude, group[0].longitude];

            group.forEach((location, index) => {
                const angle = index * angleStep;
                const jitteredLat = baseLat + offset * Math.cos(angle);
                const jitteredLng = baseLng + offset * Math.sin(angle);

                jittered.push({
                    ...location,
                    latitude: jitteredLat,
                    longitude: jitteredLng,
                });
            });
        }
    });

    return jittered;
};

const FitBounds: FC<{ locations: locationProps['locations'] }> = ({ locations }) => {
    const map = useMap();

    useEffect(() => {
        if (locations.length > 0) {
            const bounds = L.latLngBounds(locations.map((loc) => [loc.latitude, loc.longitude] as [number, number]));
            map.fitBounds(bounds, { padding: [50, 50] });
        }
    }, [locations, map]);

    return null;
};

export const Map: FC<locationProps> = ({ locations, style }) => {
    const jitteredLocations = jitterLocations(locations);
    const defaultCenter: [number, number] =
        jitteredLocations.length > 0 ? [jitteredLocations[0].latitude, jitteredLocations[0].longitude] : [38.736946, -9.142685];

    const defaultStyle = { height: '165px', width: '100%' };
    const containerStyle = style ? JSON.parse(style) : defaultStyle;

    return (
        <MapContainer center={defaultCenter} zoom={13} style={{ ...containerStyle, zIndex: 1 }} scrollWheelZoom={false}>
            <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            {jitteredLocations.map((location, index) => (
                <Marker key={index} position={[location.latitude, location.longitude]}>
                    <Popup>
                        <Anchor href={SITE_URLS.merchantDetail.replace(':uuid', `${location.uuid}`)} mode={AnchorMode.link}>
                            <strong>{location.name}</strong>
                        </Anchor>
                        <br />
                        Latitude: {Number(location.latitude).toFixed(6)}
                        <br />
                        Longitude: {Number(location.longitude).toFixed(6)}
                    </Popup>
                </Marker>
            ))}
            <FitBounds locations={jitteredLocations} />
        </MapContainer>
    );
};

export default Map;
